import { BadgeM } from "@common/components/badge/BadgeM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { setIsLiveModeEnabled } from "@data/redux/actions/liveMode"
import { liveService } from "@data/services/liveService"
import { capitalize } from "@data/utility/Utils"
import { isSubscribed } from "@data/utility/dataUtils"
import { isBlockOn, TENANT_BLOCKS } from "@data/utility/tenantBlocks"
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import React, { useContext, useEffect, useState } from "react"
import { Dimensions, SectionList, View } from "react-native"
import { ActivityIndicator, IconButton } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import ParentExtraHoursModal from "@common/components/family/ParentExtraHoursModal"
import PaymentBlockedModal from "./PaymentBlockedModal"
import StudentLiveCardComponent from "./StudentLiveCardComponent"

/**
 * Section header for date grouping
 */
const DateSectionHeader = ({ date }) => {
    const isToday = moment(date).isSame(moment(), "day")
    const isTomorrow = moment(date).isSame(moment().add(1, "day"), "day")

    let label
    if (isToday) {
        label = "Aujourd'hui"
    } else if (isTomorrow) {
        label = "Demain"
    } else {
        label = capitalize(moment(date).format("dddd D MMMM"))
    }

    return (
        <View
            style={{
                paddingVertical: 8,
                paddingHorizontal: 4,
                marginTop: 12,
            }}
        >
            <TextM
                fontWeight="Bold"
                fontSize={13}
                color={isToday ? theme.colors.success : theme.colors.grey}
            >
                {label.toUpperCase()}
            </TextM>
        </View>
    )
}

/**
 * Group occurrences by date for SectionList
 */
const groupOccurrencesByDate = (occurrences) => {
    if (!occurrences || occurrences.length === 0) return []

    const groups = {}
    occurrences.forEach((occurrence) => {
        const dateKey = moment(occurrence.date).format("YYYY-MM-DD")
        if (!groups[dateKey]) {
            groups[dateKey] = {
                date: occurrence.date,
                data: [],
            }
        }
        groups[dateKey].data.push(occurrence)
    })

    return Object.values(groups).sort((a, b) =>
        moment(a.date).diff(moment(b.date))
    )
}

/**
 * Reusable component for the "Planifier un rattrapage" recovery button
 * Styled to match "Voir tous mes lives" button
 */
const RecoveryPlannerButton = ({
    navigation,
    handleShowModal,
    withStudent,
    recoverableCount,
}) => (
    <View style={{ position: "relative", alignSelf: "center", marginTop: 10 }}>
        <FilledButtonM
            mode="outlined"
            icon="redo"
            color={
                recoverableCount > 0 ? theme.colors.error : theme.colors.primary
            }
            onPress={() => {
                navigation.push("recover-absence", {
                    withStudent: withStudent,
                })
                handleShowModal()
            }}
            label="Planifier un rattrapage"
            labelStyle={{ fontSize: 12, height: 16 }}
            contentStyle={{ height: 35 }}
        />
        {recoverableCount > 0 && (
            <BadgeM
                count={recoverableCount}
                size={18}
                style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    backgroundColor: theme.colors.error,
                    color: theme.colors.white,
                }}
            />
        )}
    </View>
)

const StudentLiveSessionsList = (props) => {
    const {
        style = {},
        noButton = false,
        sendTutorMessageTutor,
        setSendTutorMessageTutor,
        student,
        withFillerView = false,
        navigation = null,
        handleShowModal = () => {},
        handleShowSendTutorMessageModal = () => {},
    } = props

    const dimensions = useContext(DimensionsContext)

    const nav = navigation ?? useNavigation()

    // Payment blocking modal (single instance for all cards)
    const [paymentModalVisible, setPaymentModalVisible] = useState(false)

    // Self-service extra-hour modal — only used in parent view (the
    // CTA next to "Voir plus" est gatée sur `student` + abonnement actif).
    // On garde la CTA visible quand une pause manuelle est planifiée
    // (`on_grace_period_before_pause` est inclus dans `isSubscribed`) : le
    // parent doit pouvoir booker des extras avant le début de la pause ou
    // après sa reprise. Seul un abonnement réellement `paused` (actif) la
    // masque, ce qui est déjà couvert puisque `isSubscribed` exclut ce statut.
    const [extraHourModalVisible, setExtraHourModalVisible] = useState(false)
    const extraHourEnabled =
        !!student &&
        isSubscribed(student) &&
        isBlockOn(props.user, TENANT_BLOCKS.EXTRA_HOURS)

    const liveOccurrences = props.liveOccurrences

    const attendanceFlagsToBeRecovered = props.attendanceFlagsToBeRecovered
    const attendanceFlagsRecoverable = attendanceFlagsToBeRecovered?.filter(
        (flag) => flag?.is_recoverable_now
    )

    // Get first day's occurrences for parent view
    const firstDayOccurrences = React.useMemo(() => {
        if (!liveOccurrences || liveOccurrences.length === 0) return []

        const sortedOccurrences = [...liveOccurrences].sort((a, b) =>
            moment(a.date).diff(moment(b.date))
        )

        // Filter out occurrences with absence flags
        const occurrencesWithoutAbsence = sortedOccurrences.filter(
            (occurrence) => {
                const hasAbsence =
                    occurrence.live_session?.attendance_flags?.some(
                        (flag) =>
                            flag.type === "absent" &&
                            flag.user_id === student?.id &&
                            flag.date === occurrence.date
                    )
                return !hasAbsence
            }
        )

        if (occurrencesWithoutAbsence.length === 0) return []

        // Get the first date that has occurrences without absence
        const firstDate = moment(occurrencesWithoutAbsence[0].date)

        return occurrencesWithoutAbsence.filter((occurrence) =>
            moment(occurrence.date).isSame(firstDate, "day")
        )
    }, [liveOccurrences, student?.id])

    // Filter out absence occurrences (sessions the student missed) for student view
    // Only show occurrences where they're actually participating (regular or recovery)
    const studentOccurrencesFiltered = React.useMemo(() => {
        if (student || !liveOccurrences) return []

        // Filter out occurrences that have an absence flag for this student
        // (these are the missed sessions, not sessions they can attend)
        return liveOccurrences.filter((occurrence) => {
            const hasAbsenceForThisOccurrence =
                occurrence.live_session?.attendance_flags?.some(
                    (flag) =>
                        flag.type === "absent" &&
                        flag.user_id === props.user?.id &&
                        flag.date === occurrence.date &&
                        !flag.recovery_live_occurrence_id // Don't filter if it has a recovery (they're recovering it)
                )
            return !hasAbsenceForThisOccurrence
        })
    }, [student, liveOccurrences, props.user?.id])

    // Get first day's occurrences for student view (same logic as parent)
    const firstDayOccurrencesForStudent = React.useMemo(() => {
        if (student || studentOccurrencesFiltered.length === 0) return []

        const sortedOccurrences = [...studentOccurrencesFiltered].sort((a, b) =>
            moment(a.date).diff(moment(b.date))
        )

        // Get the first date
        const firstDate = moment(sortedOccurrences[0].date)

        return sortedOccurrences.filter((occurrence) =>
            moment(occurrence.date).isSame(firstDate, "day")
        )
    }, [student, studentOccurrencesFiltered])

    // Both parent and student views: Show only first day's occurrences
    const occurrencesToDisplay = student
        ? firstDayOccurrences
        : firstDayOccurrencesForStudent

    // Check if there are more occurrences beyond the first day
    const hasMoreOccurrences = student
        ? liveOccurrences?.length > firstDayOccurrences.length
        : studentOccurrencesFiltered.length >
          firstDayOccurrencesForStudent.length

    const getRequest = !student
        ? liveService.getAllMyNextLiveSessions
        : liveService.getAllMyNextLiveSessionsAsParent
    const params = !student ? {} : { student_email: student?.email }

    useEffect(() => {
        props.apiRequest(getRequest, {}, {}, params)
    }, [])

    useApiStatus(
        getRequest,
        null,
        true,
        () => {},
        () => {
            handleShowModal()
        }
    )

    const insets = useSafeAreaInsets()

    return (
        <View
            style={{
                alignItems: "center",
                ...style,
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    height: 50,
                }}
            >
                <View>
                    <TextM
                        style={{ textAlign: "flex-start" }}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        fontSize={20}
                    >
                        Mes prochains lives
                    </TextM>
                </View>
                {/* Refresh icon kept on the student-side header; the
                    parent-side relies on the auto-refetch on mount and a
                    Slack-driven push, so the manual button is gone — but
                    we still surface a spinner while the fetch is in flight. */}
                {props.getAllMyNextLiveSessionsRequest ||
                props.getAllMyNextLiveSessionsAsParentRequest ? (
                    <ActivityIndicator
                        color={theme.colors.primary}
                        size={"small"}
                    />
                ) : (
                    !student && (
                        <IconButton
                            onPress={() => {
                                props.apiRequest(getRequest, {}, {}, params)
                            }}
                            iconColor={theme.colors.primary}
                            containerColor={theme.colors.transparent}
                            icon={"sync-alt"}
                            size={20}
                        />
                    )
                )}
            </View>
            {/* Both parent and student views now show occurrences */}
            {student && liveOccurrences?.length > 0 ? (
                // Parent view: Show first day's occurrences
                <>
                    {occurrencesToDisplay?.length === 0 ? (
                        <TextM
                            fontWeight={"Light"}
                            fontSize={14}
                            wrap
                            style={{ marginTop: 20 }}
                        >
                            Aucune session planifiée
                        </TextM>
                    ) : (
                        <>
                            <SectionList
                                sections={groupOccurrencesByDate(
                                    occurrencesToDisplay
                                )}
                                keyExtractor={(occurrence) =>
                                    occurrence.id.toString()
                                }
                                renderSectionHeader={({ section }) => (
                                    <DateSectionHeader date={section.date} />
                                )}
                                renderItem={({ item: occurrence }) => {
                                    const liveSession = occurrence.live_session

                                    return (
                                        <StudentLiveCardComponent
                                            liveSessionsForOneRoom={[
                                                liveSession,
                                            ]}
                                            navigation={nav}
                                            liveSession={liveSession}
                                            occurrence={occurrence}
                                            style={{
                                                width: "100%",
                                            }}
                                            noButton={noButton}
                                            subscriptionUser={student}
                                            handleShowModal={handleShowModal}
                                            handleShowSendTutorMessageModal={
                                                handleShowSendTutorMessageModal
                                            }
                                            dateTime={occurrence?.date}
                                            sendTutorMessageTutor={
                                                sendTutorMessageTutor
                                            }
                                            setSendTutorMessageTutor={
                                                setSendTutorMessageTutor
                                            }
                                        />
                                    )
                                }}
                                scrollEnabled={false}
                                stickySectionHeadersEnabled={false}
                                style={{ width: "100%" }}
                            />
                            {extraHourEnabled && (
                                <FilledButtonM
                                    icon={"plus"}
                                    color={theme.colors.primary}
                                    onPress={() =>
                                        setExtraHourModalVisible(true)
                                    }
                                    label="Demander des heures de soutien"
                                    style={{
                                        marginTop: 20,
                                        alignSelf: "center",
                                    }}
                                    labelStyle={{ fontSize: 12, height: 16 }}
                                    contentStyle={{ height: 35 }}
                                />
                            )}
                            {hasMoreOccurrences && (
                                <FilledButtonM
                                    mode={"outlined"}
                                    icon={"calendar-alt"}
                                    color={theme.colors.primary}
                                    onPress={() => {
                                        nav.push("live-sessions-list", {
                                            student_email: student?.email,
                                        })
                                        handleShowModal()
                                    }}
                                    label="Voir plus"
                                    style={{
                                        marginTop: 10,
                                        alignSelf: "center",
                                    }}
                                    labelStyle={{ fontSize: 12, height: 16 }}
                                    contentStyle={{ height: 35 }}
                                />
                            )}
                        </>
                    )}
                    {attendanceFlagsToBeRecovered?.length > 0 && (
                        <RecoveryPlannerButton
                            navigation={nav}
                            handleShowModal={handleShowModal}
                            withStudent={!!student}
                            recoverableCount={
                                attendanceFlagsRecoverable?.length
                            }
                        />
                    )}
                </>
            ) : liveOccurrences?.length > 0 ? (
                // Student view: Show first day's occurrences with "see all" button
                <>
                    <SectionList
                        sections={groupOccurrencesByDate(occurrencesToDisplay)}
                        keyExtractor={(occurrence) => occurrence.id.toString()}
                        renderSectionHeader={({ section }) => (
                            <DateSectionHeader date={section.date} />
                        )}
                        renderItem={({ item: occurrence }) => {
                            const liveSession = occurrence.live_session

                            return (
                                <StudentLiveCardComponent
                                    liveSessionsForOneRoom={[liveSession]}
                                    navigation={nav}
                                    liveSession={liveSession}
                                    occurrence={occurrence}
                                    style={{
                                        width: dimensions?.width - 40,
                                    }}
                                    noButton={noButton}
                                    subscriptionUser={student}
                                    handleShowModal={handleShowModal}
                                    handleShowSendTutorMessageModal={
                                        handleShowSendTutorMessageModal
                                    }
                                    dateTime={occurrence?.date}
                                    sendTutorMessageTutor={
                                        sendTutorMessageTutor
                                    }
                                    setSendTutorMessageTutor={
                                        setSendTutorMessageTutor
                                    }
                                    onPaymentBlockedPress={() =>
                                        setPaymentModalVisible(true)
                                    }
                                />
                            )
                        }}
                        scrollEnabled={false}
                        stickySectionHeadersEnabled={false}
                        style={{ width: "100%" }}
                    />
                    {hasMoreOccurrences && (
                        <FilledButtonM
                            mode={"outlined"}
                            icon={"calendar-alt"}
                            color={theme.colors.primary}
                            onPress={() => {
                                nav.push("live-sessions-list")
                                handleShowModal()
                            }}
                            label="Voir tous mes lives"
                            style={{
                                marginTop: 20,
                                alignSelf: "center",
                            }}
                            labelStyle={{ fontSize: 12, height: 16 }}
                            contentStyle={{ height: 35 }}
                        />
                    )}
                    {!student && attendanceFlagsToBeRecovered?.length > 0 && (
                        <RecoveryPlannerButton
                            navigation={nav}
                            handleShowModal={handleShowModal}
                            withStudent={false}
                            recoverableCount={
                                attendanceFlagsRecoverable?.length
                            }
                        />
                    )}
                </>
            ) : props.getAllMyNextLiveSessionsRequest ||
              props.getAllMyNextLiveSessionsAsParentRequest ? null : (
                // No occurrences - show empty state
                <>
                    <TextM
                        fontWeight={"Light"}
                        fontSize={14}
                        wrap
                        style={{ marginTop: 20 }}
                    >
                        Aucune session planifiée
                    </TextM>
                    {extraHourEnabled && (
                        <FilledButtonM
                            icon={"plus"}
                            color={theme.colors.primary}
                            onPress={() => setExtraHourModalVisible(true)}
                            label="Demander des heures de soutien"
                            style={{
                                marginTop: 20,
                                alignSelf: "center",
                            }}
                            labelStyle={{ fontSize: 12, height: 16 }}
                            contentStyle={{ height: 35 }}
                        />
                    )}
                    {attendanceFlagsToBeRecovered?.length > 0 && (
                        <RecoveryPlannerButton
                            navigation={nav}
                            handleShowModal={handleShowModal}
                            withStudent={!!student}
                            recoverableCount={
                                attendanceFlagsRecoverable?.length
                            }
                        />
                    )}
                </>
            )}
            {withFillerView && (
                <View
                    style={{
                        height: Math.max(
                            0,
                            Dimensions.get("window")?.height -
                                (50 + // header size
                                    (IS_WEB ? 74 : 80) +
                                    insets.bottom / 2 + // bottom tabs size
                                    90 + // fixed part
                                    (occurrencesToDisplay?.length || 0) * 160)
                        ),
                    }}
                />
            )}
            {/* Payment blocked modal */}
            <PaymentBlockedModal
                visible={paymentModalVisible}
                onDismiss={() => {
                    setPaymentModalVisible(false)
                    props.apiRequest(getRequest, {}, {}, params)
                }}
                paymentUrl={props.paymentUrl}
            />
            {extraHourEnabled && (
                <ParentExtraHoursModal
                    visible={extraHourModalVisible}
                    setVisible={setExtraHourModalVisible}
                />
            )}
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        getAllMyNextLiveSessionsRequest:
            state.api.getAllMyNextLiveSessions?.request,
        getAllMyNextLiveSessionsAsParentRequest:
            state.api.getAllMyNextLiveSessionsAsParent?.request,
        getUserSuccess: state.api.getUser?.success,
        getUserError: state.api.getUser?.error,
        activityTemplates: state.data.staticData?.activity_templates,
        user: state.data.currentUser?.object,
        maxPrioritizedSchoolSubjects:
            state.data.staticData?.max_prioritized_school_subjects,
        isLive: state.liveMode.isLiveModeEnabled,
        getLiveSessionRequest: state.api.getLiveSession?.request,
        liveSessions: state.data.liveSessions?.full_list,
        liveOccurrences: state.data.liveSessions?.live_occurrences,
        attendanceFlagsToBeRecovered:
            state.data.liveSessions?.attendance_flags_to_be_recovered,
        paymentUrl: state.data.currentUser?.object?.payment_url,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setIsLiveModeEnabled,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(StudentLiveSessionsList)
